import { DataTable } from "@/components/tables";
import { Stack } from "@altimateai/lego";
import { DbtIcon } from "@assets/icons";
import { formatCost, formatMilliseconds } from "@/helpers/utils";
import dayjs from "dayjs";
import { ColorInsightTags } from "@/components/tags";
import { Badge } from "@altimateai/lego";
import { FilterType } from "@/components/tables/types";
import { Paginate } from "@/components/paginate";
import { EXECUTION_STATUS_OPTIONS, ExecutionStatus } from "@/pages/query/constants";
import { FC, useMemo } from "react";
import { useAppState } from "@/modules/app/useAppContext";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { DbtJobRunItem } from "@/pages/query/types";

interface CommonTaskRunsTableProps {
  items: DbtJobRunItem[];
  isLoading: boolean;
}

export const CommonTaskRunsTable: FC<CommonTaskRunsTableProps> = ({ items, isLoading }) => {
  const { currency } = useAppState();
  const { getFilterValue } = useDatatableContext();
  const { page, pageSize, setPage, setPageSize, totalPages, totalItems } = useTablePagination();

  // Get filter values from datatable context
  const executionStatusFilter = useMemo(
    () => getFilterValue<string>("executionStatus") || [],
    [getFilterValue]
  );

  return (
    <>
      <DataTable<DbtJobRunItem>
        isLoading={isLoading}
        getRowUrl={item => (item.query_id ? `/query/${item.query_id}` : "")}
        getRowState={item => ({ entityName: item.run_id })}
        resetPage={() => {}}
        data={{
          items: items ?? [],
        }}
        header={[
          {
            id: 1,
            label: "Run ID",
            uid: "run_id",
            width: "25%",
            render: item => (
              <Stack align={"center"} gap={"sm"}>
                <DbtIcon className={"al-h-4 al-w-4"} />
                {item.run_id}
              </Stack>
            ),
          },
          {
            id: 2,
            label: "Run Cost",
            uid: "query_cost",
            render: item => <div>{formatCost(item.query_cost, currency)}</div>,
            sort: {
              sortSearchKey: "sortAttribute",
            },
          },
          {
            id: 4,
            label: "Start Time",
            uid: "start_time",
            render: item => <div>{dayjs(item.start_time).format("YYYY-MM-DD hh:mm a")}</div>,
            sort: {
              sortSearchKey: "sortAttribute",
            },
          },
          {
            id: 5,
            label: "Execution Time",
            uid: "execution_time",
            render: item => <div>{formatMilliseconds(item.execution_time)}</div>,
            sort: {
              sortSearchKey: "sortAttribute",
            },
          },
          {
            id: 6,
            label: "Insights",
            uid: "insights",
            render: item => (
              <ColorInsightTags onClick={() => {}} tags={item?.insights ?? []} showMore />
            ),
          },
          {
            id: 7,
            label: "Run status",
            uid: "execution_status",
            render: item =>
              item.execution_status === ExecutionStatus.SUCCESS ? (
                <Badge className={"!al-bg-action-green"}>Success</Badge>
              ) : (
                <Badge variant={"destructive"}>Failed</Badge>
              ),
            filter: {
              id: "execution_status",
              label: "Run Status",
              searchKey: "executionStatus",
              filterType: FilterType.DROPDOWN,
              value: executionStatusFilter,
              options: EXECUTION_STATUS_OPTIONS,
            },
          },
        ]}
      />
      <Paginate
        isLoading={isLoading}
        itemCount={totalItems}
        numPages={totalPages}
        page={page}
        pageSize={pageSize}
        onPageClick={setPage}
        onPageSizeChange={setPageSize}
        showPageSize
      />
    </>
  );
};
